import { formatSalesOrderNumber } from '@order-management/format';
import {
  DeferredResponse,
  InputType,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  SalesOrder,
  SortDirection,
  UserMasqueradeSalesOrderCriteria,
  UserRoleType,
} from '@order-management/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Columns, Element } from 'react-bulma-components';
import { FaArrowRight } from 'react-icons/fa6';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
} from 'react-router-dom';
import SalesOrdersTable from '../../sales-orders/sales-orders-table/sales-orders-table';
import BackButton from '../../shared/back-button/back-button';
import Conditional from '../../shared/conditional/conditional';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useHttpRequest from '../../shared/hooks/http-request';
import useUser from '../../shared/hooks/user';
import If from '../../shared/if/if';
import { formatDateToLocaleString } from '../../shared/utils';
import BoxLayout from '../../ui/box-layout/box-layout';
import Button from '../../ui/button/button';
import FeedbackMessage from '../../ui/feedback-message/feedback-message';
import Link from '../../ui/link/link';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import SelectionIndicator from '../../ui/selection-indicator/selection-indicator';
import { Column, OnTableChangeData, RowSelection } from '../../ui/table/types';
import {
  fetchAvailableSalesOrdersToMasquerade,
  switchRole,
} from '../../users/users-service';

const SWITCH_ROLE_ERROR_MESSAGE = 'Could not switch role. Please try again';
const TITLE = 'Manage customer’s sales orders';
const SUBTITLE =
  'Select the sales orders you want to manage. Please note all SOs that show up here are the ones for which you have been granted Temporary Manager access';
const NO_SALES_ORDERS_ERROR_MESSAGE =
  'You must select at least one Sales Order to manage';
const FETCH_AVAILABLE_SALES_ORDERS_ERROR_MESSAGE =
  'Available sales orders could not be fetched. Please try again';

const ITEMS_PER_PAGE_OPTIONS = [10, 20, 30, 50];

const initialCriteria: UserMasqueradeSalesOrderCriteria = {
  itemsPerPage: 50,
  pageNumber: 1,
  search: '',
  sortBy: 'opportunity.account.name',
  sortDirection: SortDirection.ASC,
};

export const loader: LoaderFunction = async () => {
  const response = fetchAvailableSalesOrdersToMasquerade(initialCriteria);

  return defer({ response });
};

export function SelectMasqueradeSalesOrdersPageContent() {
  const { user } = useUser();
  const { executeHttpRequest: executeSwitchRole, isLoading: isSwitchingRole } =
    useHttpRequest();
  const {
    executeHttpRequest: executeFetchAvailableSalesOrders,
    isLoading: isFetchingAvailableSalesOrders,
  } = useHttpRequest();
  const availableSalesOrdersInitialResponse =
    useAsyncValue() as PaginatedAPIResponse<SalesOrder>;
  const [availableSalesOrdersResponse, setAvailableSalesOrdersResponse] =
    useState(availableSalesOrdersInitialResponse);
  const [availableSalesOrders, setAvailableSalesOrders] = useState<
    SalesOrder[]
  >(availableSalesOrdersResponse.data);
  const [selectedSalesOrders, setSelectedSalesOrders] = useState(
    new Map<string, SalesOrder>()
  );
  const [nextButtonPressed, setNextButtonPressed] = useState(false);
  const [criteria, setCriteria] = useState(initialCriteria);

  const availableSalesOrdersMap = useMemo(
    () =>
      new Map<string, SalesOrder>(
        availableSalesOrders.map((so) => [so.id, so])
      ),
    [availableSalesOrders]
  );

  const tableConfig: PaginatedContentConfig = useMemo(
    () => ({
      itemsPerPage: criteria.itemsPerPage,
      pageNumber: criteria.pageNumber,
      search: criteria.search,
      sortBy: criteria.sortBy,
      sortDirection: criteria.sortDirection,
      pages: availableSalesOrdersResponse.pages,
    }),
    [
      availableSalesOrdersResponse.pages,
      criteria.itemsPerPage,
      criteria.pageNumber,
      criteria.search,
      criteria.sortBy,
      criteria.sortDirection,
    ]
  );

  const columns: Column<SalesOrder>[] = useMemo(
    () => [
      {
        key: 'number',
        className: 'is-width-15',
        render: (el) => (
          <Link to={`/sales-orders/${el.id}`} openNewTab>
            {formatSalesOrderNumber(el.number)}
          </Link>
        ),
        title: 'Sales Order',
        sortable: true,
      },
      {
        key: 'opportunity.account.name',
        className: 'is-width-25',
        render: (el) => (
          <Link to={`/accounts/${el.opportunity?.account?.id}`} openNewTab>
            {el.opportunity?.account?.name}
          </Link>
        ),
        title: 'Account',
        sortable: true,
      },
      {
        key: 'opportunity.name',
        className: 'is-width-25',
        render: (el) => el.opportunity?.name,
        title: 'Opportunity Name',
        sortable: true,
      },
      {
        key: 'closeDate',
        className: 'is-width-10',
        render: (el) =>
          el.closeDate ? formatDateToLocaleString(el.closeDate) : '',
        title: 'Close date',
        sortable: false,
      },
      {
        key: 'user',
        className: 'is-width-25',
        dataIndex: 'endUser',
        title: 'End user',
        sortable: false,
      },
    ],
    []
  );

  const rowSelection: RowSelection = useMemo(() => {
    return {
      onChange: (selectedRowIds) => {
        selectedRowIds.forEach((id) => {
          setSelectedSalesOrders((oldSelectedSalesOrders) => {
            const newSelectedSalesOrders = new Map(oldSelectedSalesOrders);
            if (oldSelectedSalesOrders?.has(id)) {
              newSelectedSalesOrders.delete(id);
            } else {
              const newSalesOrder = availableSalesOrdersMap.get(id);
              newSelectedSalesOrders.set(id, newSalesOrder!);
            }
            return newSelectedSalesOrders;
          });
        });
      },
      selectedRowKeys: availableSalesOrders
        .filter((availableSalesOrder) =>
          selectedSalesOrders.has(availableSalesOrder.id)
        )
        .map((availableSalesOrder) => availableSalesOrder.id),
    };
  }, [availableSalesOrders, availableSalesOrdersMap, selectedSalesOrders]);

  const filtersData = useMemo(() => {
    return {
      salesOrders: {
        title: 'Sales order',
        appliedCount: criteria.salesOrders?.length,
        value: criteria.salesOrders,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'salesOrders',
          valueKey: 'salesOrders',
          placeholder: 'Search by SO',
        },
      },
      accounts: {
        title: 'Account',
        appliedCount: criteria.accounts?.length,
        value: criteria.accounts,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'accounts',
          valueKey: 'accounts',
          placeholder: 'Search by Account',
        },
      },
      endUser: {
        title: 'End user',
        appliedCount: criteria.endUser?.length,
        value: criteria.endUser,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'endUser',
          valueKey: 'endUser',
          placeholder: 'Search by End user',
        },
      },
    };
  }, [
    criteria.accounts,
    criteria.endUser,
    criteria.salesOrders,
  ]) as FiltersData<UserMasqueradeSalesOrderCriteria>;

  const hasSalesOrdersSelected = useMemo(
    () => selectedSalesOrders.size > 0,
    [selectedSalesOrders.size]
  );

  const showNoSalesOrderSelectedError = useMemo(
    () => !hasSalesOrdersSelected && nextButtonPressed,
    [hasSalesOrdersSelected, nextButtonPressed]
  );

  const emptySalesOrdersContentText = useMemo(() => {
    const hasAppliedFilters =
      criteria.search !== '' ||
      Object.entries(filtersData).some(([_, filter]) => filter.appliedCount);
    if (hasAppliedFilters) {
      return 'There are no sales orders that match the selected criteria';
    }
    return 'There are no sales orders at the moment';
  }, [criteria.search, filtersData]);

  const fetchAvailableSalesOrders = useCallback(
    async (criteria: UserMasqueradeSalesOrderCriteria) =>
      executeFetchAvailableSalesOrders({
        asyncFunction: async () => {
          const response = await fetchAvailableSalesOrdersToMasquerade(
            criteria
          );
          setAvailableSalesOrders(response.data);
          setAvailableSalesOrdersResponse(response);
        },
        customErrorMessage: FETCH_AVAILABLE_SALES_ORDERS_ERROR_MESSAGE,
      }),
    [executeFetchAvailableSalesOrders]
  );

  const handleSwitchRole = useCallback(async () => {
    if (hasSalesOrdersSelected) {
      const userRoleToSwitch = user.roles.find(
        (roleAssignment) => roleAssignment.role.name === UserRoleType.CUSTOMER
      );
      return executeSwitchRole({
        asyncFunction: async () => {
          await switchRole(
            userRoleToSwitch!.id,
            Array.from(selectedSalesOrders.keys())
          );
          window.location.replace('/');
        },
        customErrorMessage: SWITCH_ROLE_ERROR_MESSAGE,
      });
    } else {
      setNextButtonPressed(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [
    executeSwitchRole,
    hasSalesOrdersSelected,
    selectedSalesOrders,
    user.roles,
  ]);

  const handleTableFilterSubmit = useCallback(
    async (tableFilterValue: UserMasqueradeSalesOrderCriteria) => {
      const { itemsPerPage, pageNumber } = initialCriteria;
      const newCriteria = {
        ...tableFilterValue,
        itemsPerPage,
        pageNumber,
      };
      setCriteria(newCriteria);
      await fetchAvailableSalesOrders(newCriteria);
    },
    [fetchAvailableSalesOrders]
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof UserMasqueradeSalesOrderCriteria)[]) => {
      const newData = { ...criteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteria[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newData[filterDataKey];
        } else {
          newData[filterDataKey] = initialFilterValue as string &
            string[] &
            boolean;
        }
      }
      handleTableFilterSubmit(newData);
    },
    [criteria, handleTableFilterSubmit]
  );

  const onTableChange = useCallback(
    async (value: OnTableChangeData<SalesOrder>) => {
      const newCriteria = {
        ...value.config,
        sortBy: value.column?.key || criteria.sortBy,
      };

      setCriteria(newCriteria);
      await fetchAvailableSalesOrders(newCriteria);
    },
    [criteria, fetchAvailableSalesOrders]
  );

  return (
    <BoxLayout
      className="is-min-height-80 mx-10 my-4"
      header={
        <Columns marginless>
          <Columns.Column className="pl-4">
            <BackButton />
            <h1 className="is-size-3 has-text-weight-light">{TITLE}</h1>
            <h2 className="is-size-5 has-text-weight-light">{SUBTITLE}</h2>
          </Columns.Column>
          <Columns.Column
            display="flex"
            justifyContent="flex-end"
            className="is-narrow"
          >
            <Button
              color="primary-dark"
              onClick={handleSwitchRole}
              isExecutingAction={isSwitchingRole}
            >
              <span>Next</span>
              <Button.LoadingIcon icon={FaArrowRight} />
            </Button>
          </Columns.Column>
        </Columns>
      }
    >
      <Element display="flex" className="w-full">
        <FilterBar
          initialData={initialCriteria}
          data={criteria}
          filtersData={filtersData}
          onFilterSubmit={handleTableFilterSubmit}
          onRemoveFilter={onRemoveFilters}
        />
      </Element>
      <If condition={showNoSalesOrderSelectedError}>
        <Element mb={3}>
          <FeedbackMessage>{NO_SALES_ORDERS_ERROR_MESSAGE}</FeedbackMessage>
        </Element>
      </If>
      <Conditional condition={isFetchingAvailableSalesOrders}>
        <Conditional.True>
          <PageSpinner />
        </Conditional.True>
        <Conditional.False>
          <SalesOrdersTable
            bordered={false}
            onChange={onTableChange}
            dataSource={availableSalesOrders}
            config={tableConfig}
            columns={columns}
            rowSelection={rowSelection}
            rowLabel={(el) => formatSalesOrderNumber(el.number)}
            empty={
              <Element
                display="flex"
                justifyContent="center"
                className="my-2 has-text-weight-light"
              >
                <span>{emptySalesOrdersContentText}</span>
              </Element>
            }
            form={{
              itemsPerPageOptions: ITEMS_PER_PAGE_OPTIONS,
              leftExtra: (
                <SelectionIndicator
                  selected={selectedSalesOrders.size}
                  total={availableSalesOrdersResponse.total}
                />
              ),
            }}
          />
        </Conditional.False>
      </Conditional>
    </BoxLayout>
  );
}

export function SelectMasqueradeSalesOrdersPage() {
  const { response } = useLoaderData() as DeferredResponse<
    Promise<PaginatedAPIResponse<SalesOrder>>
  >;

  return (
    <Suspense fallback={<PageSpinner />}>
      <ErrorHandlingAwait resolve={response}>
        <SelectMasqueradeSalesOrdersPageContent />
      </ErrorHandlingAwait>
    </Suspense>
  );
}

export default SelectMasqueradeSalesOrdersPage;
